import { useScienceTutorSection, tutorSelection } from './scienceTutorContext';
import { Leaf } from 'lucide-react';
import { useState } from 'react';

import { formatSig } from './energyCalorimetry';
import {
  COMPARISON_LABELS,
  FUEL_CASES,
  ORIGIN_LABELS,
  RENEWABILITY_LABELS,
  checkFuelCasePrediction,
  fuelProfile,
  prettyChemistry,
  type EmissionsComparison,
  type FuelCase,
  type Renewability,
} from './fuelsSustainability';
import { CaseList, FeedbackPanel, OptionButton, ValueBar } from './FuelsSustainabilityShared';

const RENEWABILITY_OPTIONS: Renewability[] = ['renewable', 'non-renewable'];
const COMPARISON_OPTIONS: EmissionsComparison[] = ['less', 'about-same', 'more'];

const PROFILES = FUEL_CASES.map((entry) => ({ entry, profile: fuelProfile(entry) }));
const MAX_ENERGY = Math.max(...PROFILES.map(({ profile }) => profile?.energyPerGram ?? 0));
const MAX_CO2 = Math.max(...PROFILES.map(({ profile }) => profile?.co2PerMJ ?? 0));

function ComparisonCharts({ selected }: { selected: FuelCase }) {
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <figure className="min-w-0 rounded-2xl border border-hairline bg-surface p-4">
        <figcaption className="text-sm font-extrabold text-ink">Energy released per gram</figcaption>
        <p className="text-xs font-medium text-slate2">|ΔcH| ÷ M, kJ g⁻¹</p>
        <div className="mt-3 grid gap-1.5">
          {PROFILES.map(({ entry, profile }) =>
            profile ? (
              <ValueBar
                key={entry.id}
                label={entry.name}
                value={profile.energyPerGram}
                max={MAX_ENERGY}
                valueText={formatSig(profile.energyPerGram, 3)}
                highlight={entry.id === selected.id}
                tone="sky"
              />
            ) : null,
          )}
        </div>
      </figure>
      <figure className="min-w-0 rounded-2xl border border-hairline bg-surface p-4">
        <figcaption className="text-sm font-extrabold text-ink">CO₂ released when it burns</figcaption>
        <p className="text-xs font-medium text-slate2">Stoichiometric, g CO₂ per MJ of heat</p>
        <div className="mt-3 grid gap-1.5">
          {PROFILES.map(({ entry, profile }) =>
            profile ? (
              <ValueBar
                key={entry.id}
                label={entry.name}
                value={profile.co2PerMJ}
                max={MAX_CO2}
                valueText={formatSig(profile.co2PerMJ, 3)}
                highlight={entry.id === selected.id}
                tone="coral"
              />
            ) : null,
          )}
        </div>
      </figure>
    </div>
  );
}

export function FuelsSustainabilityFossilBio() {
  const [selected, setSelected] = useState<FuelCase>(FUEL_CASES[0]);
  const [renewability, setRenewability] = useState<Renewability | null>(null);
  const [comparison, setComparison] = useState<EmissionsComparison | null>(null);
  const [revealed, setRevealed] = useState(false);
  useScienceTutorSection('FuelsSustainabilityFossilBio', { selected: tutorSelection(selected), renewability, comparison, revealed });
  const profile = fuelProfile(selected);
  const check =
    revealed && renewability && comparison
      ? checkFuelCasePrediction(selected, renewability, comparison)
      : null;

  const choose = (id: string) => {
    const next = FUEL_CASES.find((entry) => entry.id === id);
    if (!next) return;
    setSelected(next);
    setRenewability(null);
    setComparison(null);
    setRevealed(false);
  };

  return (
    <div className="grid gap-6 xl:grid-cols-[320px_minmax(0,1fr)]">
      <CaseList
        heading="Choose a fuel"
        label="Fuels"
        items={FUEL_CASES.map((entry) => ({ id: entry.id, label: entry.name }))}
        selectedId={selected.id}
        onSelect={choose}
      />

      <section className="min-w-0 rounded-[1.75rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6">
        <h3 className="text-xl font-extrabold text-ink">{selected.name}</h3>
        <p className="mt-1 text-sm font-medium leading-6 text-slate2">
          <strong className="text-ink">Made from:</strong> {selected.feedstock}
        </p>
        {profile ? (
          <p className="mt-2 font-mono text-sm font-bold text-ink">
            {profile.fuel.formula} · M = {profile.fuel.molarMass} g mol⁻¹ · ΔcH ={' '}
            {profile.fuel.combustionkJPerMol} kJ mol⁻¹
          </p>
        ) : (
          <p className="mt-2 text-sm font-bold text-ink">This fuel has no reviewed data.</p>
        )}

        <div className="mt-5 grid gap-5 lg:grid-cols-2">
          <fieldset>
            <legend className="text-sm font-extrabold text-ink">1. Renewable or not?</legend>
            <div className="mt-2 grid gap-2">
              {RENEWABILITY_OPTIONS.map((option) => (
                <OptionButton
                  key={option}
                  pressed={renewability === option}
                  onClick={() => {
                    setRenewability(option);
                    setRevealed(false);
                  }}
                >
                  {RENEWABILITY_LABELS[option]}
                </OptionButton>
              ))}
            </div>
          </fieldset>
          <fieldset>
            <legend className="text-sm font-extrabold text-ink">
              2. CO₂ per MJ when it burns, compared with natural gas
            </legend>
            <div className="mt-2 grid gap-2">
              {COMPARISON_OPTIONS.map((option) => (
                <OptionButton
                  key={option}
                  pressed={comparison === option}
                  onClick={() => {
                    setComparison(option);
                    setRevealed(false);
                  }}
                >
                  {COMPARISON_LABELS[option]}
                </OptionButton>
              ))}
            </div>
          </fieldset>
        </div>

        <button
          type="button"
          disabled={!renewability || !comparison || !profile}
          onClick={() => setRevealed(true)}
          className="btn-pill-primary mt-5 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Reveal the model
        </button>

        {check && profile ? (
          <div className="mt-6 grid gap-4">
            <FeedbackPanel
              good={check.renewabilityCorrect && check.comparisonCorrect}
              label="Fuel feedback"
              title={
                check.renewabilityCorrect && check.comparisonCorrect
                  ? 'Both predictions match the model.'
                  : 'Compare your predictions with the model.'
              }
            >
              <p>
                <strong className="text-ink">
                  Source: {check.renewabilityCorrect ? 'correct' : 'check again'}.
                </strong>{' '}
                {check.renewabilityFeedback}
              </p>
              <p className="mt-2">
                <strong className="text-ink">
                  Emissions: {check.comparisonCorrect ? 'correct' : 'check again'}.
                </strong>{' '}
                {check.comparisonFeedback}
              </p>
            </FeedbackPanel>
            <div className="rounded-2xl border border-hairline bg-surface p-4">
              <p className="flex items-center gap-2 text-sm font-extrabold text-ink">
                <Leaf className="h-4 w-4 text-brand-mint" aria-hidden="true" />
                {ORIGIN_LABELS[selected.origin]} · {RENEWABILITY_LABELS[selected.renewability]}
              </p>
              <p className="mt-2 break-words font-mono text-sm font-bold text-ink">
                {prettyChemistry(profile.equation)}
              </p>
              <p className="mt-2 text-sm font-medium text-slate2">
                {formatSig(profile.energyPerGram, 3)} kJ g⁻¹ · {formatSig(profile.co2PerMJ, 3)} g
                CO₂ per MJ
              </p>
            </div>
            <ComparisonCharts selected={selected} />
          </div>
        ) : null}
      </section>
    </div>
  );
}
